import React from 'react';
import css from './TemplateCard.module.css';
import { TemplateCategoryType } from '@shared/Enums/TemplateCategoryEnum';
import { TemplateEntity } from '@entities/Template/Types';
import { GetImageFromGalleries } from '@modules/Steps/Shared/Helpers/GetImageFromGalleries';
import { DEFAULT_TEMPLATE_IMG } from '@shared/Constants/Images/DefaultTemplateImg';
import { useNavigate } from 'react-router-dom';
import { TEMPLATE_PRICE } from '@shared/Constants/TemplatePrice';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { useMediaType } from '@shared/Hooks/useMediaTypeHook';
import { ROUTES } from '@configs/AppRoutes';
import classNames from 'classnames';
import EyeIcon from '@shared/Icons/EyeIcon';
import { HandleImageError } from '@shared/Helpers/HandleImageError';
import { TruncateText } from '@shared/Helpers/FormatValue/TruncateText/TruncateText';

interface TemplateCardProps {
    template: TemplateEntity;
    category: TemplateCategoryType;
    isLandingExample?: boolean;

    extraCardContentClassName?: string;
    extraTemplateTitleClassName?: string;
    extraImgClassName?: string;
    extraCardDescriptionClassName?: string;
    extraTemplateInfoClassName?: string;
    extraTemplateInfoPlaceholderBlockClassName?: string;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
    template,
    category,
    isLandingExample = false,

    extraCardContentClassName,
    extraImgClassName,
    extraTemplateInfoClassName,
    extraCardDescriptionClassName,
    extraTemplateInfoPlaceholderBlockClassName,
    extraTemplateTitleClassName,
}) => {
    const imgUrl = GetImageFromGalleries(
        DEFAULT_TEMPLATE_IMG,
        template.galleries
    );

    const navigate = useNavigate();
    const [isDesktop] = useMediaType();

    const handleViewTemplateInfo = () => {
        navigate(ROUTES.TEMPLATE_INFO.getPath(`${template.id}`));
    };

    const templatePrice = template.paid ? TEMPLATE_PRICE : 0;

    return (
        <div
            className={classNames(
                css.templateCardContent,
                extraCardContentClassName
            )}
        >
            <div className={css.templateCardImgContainer}>
                <img
                    src={imgUrl}
                    alt={template.title}
                    sizes="(min-width: 1024px) 365px, 100vw"
                    className={classNames(
                        css.templateCardImg,
                        extraImgClassName
                    )}
                    onError={HandleImageError(DEFAULT_TEMPLATE_IMG)}
                    onClick={handleViewTemplateInfo}
                />
                {isLandingExample && (
                    <BasicButton
                        onClick={handleViewTemplateInfo}
                        className={css.landingBtn}
                    >
                        <EyeIcon
                            height={38}
                            width={38}
                            fill={'var(--primary-500)'}
                        />
                    </BasicButton>
                )}
            </div>

            <div
                className={classNames(
                    css.cardDescription,
                    {
                        [css.landingDescription]: isLandingExample,
                    },
                    extraCardDescriptionClassName
                )}
            >
                <div
                    className={classNames(
                        css.templateInfo,
                        {
                            [css.landingText]: isLandingExample,
                        },
                        extraTemplateInfoClassName
                    )}
                >
                    <div
                        className={classNames(
                            css.templateInfoTitle,
                            extraTemplateTitleClassName
                        )}
                        onClick={handleViewTemplateInfo}
                    >
                        {TruncateText(template.title, 17)}
                    </div>
                    {(isDesktop || isLandingExample) && (
                        <div className={css.templateInfoCategory}>
                            {category}
                        </div>
                    )}
                </div>

                <div
                    className={classNames(
                        css.templateInfoPlaceholderBlock,
                        {
                            [css.landingText]: isLandingExample,
                        },
                        extraTemplateInfoPlaceholderBlockClassName
                    )}
                >
                    <div className={css.templateInfoPrice}>
                        {Number(templatePrice) < 1
                            ? 'Free'
                            : `R ${templatePrice}`}
                    </div>
                    <div>VAT included*</div>
                </div>
            </div>

            {!isLandingExample && (
                <BasicButton
                    onClick={handleViewTemplateInfo}
                    extraClassName={css.btn}
                >
                    VIEW THIS TEMPLATE
                </BasicButton>
            )}
        </div>
    );
};

export default TemplateCard;
